import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { z } from "zod";

/** How far around your active home the community feed reaches, in miles. */
export const FEED_RADIUS_MILES = 25;

type Geo = { lat: number | null; lng: number | null; town: string | null };

function isAllowedShieldsReturnUrl(value: string): boolean {
  try {
    const url = new URL(value);
    const host = url.hostname.toLowerCase();
    if (url.protocol !== "https:" && host !== "localhost" && host !== "127.0.0.1") return false;
    return host === "bookwithshields.com"
      || host === "www.bookwithshields.com"
      || host.endsWith(".lovable.app")
      || host === "localhost"
      || host === "127.0.0.1";
  } catch {
    return false;
  }
}

/** Looks up the rough centre of a UK postcode. Never stores an exact house pin. */
async function geocodePostcode(postcode: string): Promise<Geo> {
  try {
    const res = await fetch(
      `https://api.postcodes.io/postcodes/${encodeURIComponent(postcode.trim())}`,
    );
    if (!res.ok) return { lat: null, lng: null, town: null };
    const json = (await res.json()) as {
      result?: { latitude: number; longitude: number; admin_district?: string };
    };
    return {
      lat: json.result?.latitude ?? null,
      lng: json.result?.longitude ?? null,
      town: json.result?.admin_district ?? null,
    };
  } catch {
    return { lat: null, lng: null, town: null };
  }
}

/** Every address this member has saved, active one first. */
export const listHomes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("saved_homes")
      .select("*")
      .eq("user_id", context.userId)
      .order("is_active", { ascending: false })
      .order("created_at", { ascending: true });
    return (data ?? []).map((h) => ({
      id: h.id,
      label: h.label,
      addressLine: h.address_line,
      townCity: h.town_city,
      postcode: h.postcode,
      lat: h.lat,
      lng: h.lng,
      isActive: h.is_active,
    }));
  });

/** Save another address. The database enforces the membership property limit atomically. */
export const addHome = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        label: z.string().trim().min(1).max(40).default("Home"),
        addressLine: z.string().trim().max(120).optional(),
        postcode: z.string().trim().min(3).max(10),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const geo = await geocodePostcode(data.postcode);
    const { data: result, error } = await (context.supabase as any).rpc("create_saved_home", {
      p_label: data.label,
      p_address_line: data.addressLine ?? null,
      p_town_city: geo.town,
      p_postcode: data.postcode,
      p_lat: geo.lat,
      p_lng: geo.lng,
    });
    if (error) return { ok: false as const, error: error.message };
    const response = result as { ok?: boolean; error?: string; limit?: number } | null;
    if (!response?.ok) {
      return {
        ok: false as const,
        error: response?.error === "property_limit_reached"
          ? `Your membership includes ${response.limit ?? 1} ${response.limit === 1 ? "property" : "properties"}. Upgrade to add another.`
          : response?.error ?? "Could not save this property",
      };
    }
    return { ok: true as const };
  });

/** Update an owned saved home; the owner filter prevents editing shared or other homes. */
export const updateHomeDetails = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({
    homeId: z.string().uuid(),
    label: z.string().trim().min(1).max(40),
    addressLine: z.string().trim().max(120),
    postcode: z.string().trim().regex(/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i, "Enter a valid UK postcode"),
  }).parse(value))
  .handler(async ({ data, context }) => {
    const postcode = data.postcode.toUpperCase().replace(/\s+/g, "");
    const formattedPostcode = `${postcode.slice(0, -3)} ${postcode.slice(-3)}`;
    const geo = await geocodePostcode(formattedPostcode);
    const { data: updated, error } = await context.supabase
      .from("saved_homes")
      .update({
        label: data.label,
        address_line: data.addressLine || null,
        postcode: formattedPostcode,
        town_city: geo.town,
        lat: geo.lat,
        lng: geo.lng,
      })
      .eq("id", data.homeId)
      .eq("user_id", context.userId)
      .select("id")
      .maybeSingle();
    if (error) return { ok: false as const, error: error.message };
    if (!updated) return { ok: false as const, error: "Property not found" };
    return { ok: true as const };
  });

/** Switch which saved address the feed and booking flow use. */
export const setActiveHome = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ homeId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase.rpc("set_active_home", { _home_id: data.homeId });
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

export const removeHome = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => z.object({ homeId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    const { error } = await context.supabase
      .from("saved_homes")
      .delete()
      .eq("id", data.homeId)
      .eq("user_id", context.userId);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });

/** The active home (or a partner's base) used to centre the feed. */
export const getFeedCentre = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: home } = await context.supabase
      .from("saved_homes")
      .select("label, town_city, postcode, lat, lng")
      .eq("user_id", context.userId)
      .eq("is_active", true)
      .maybeSingle();
    if (home?.lat != null) {
      return {
        label: home.town_city || home.postcode,
        lat: home.lat,
        lng: home.lng,
        radiusMiles: FEED_RADIUS_MILES,
      };
    }
    const { data: partner } = await context.supabase
      .from("partner_profiles")
      .select("base_postcode, base_lat, base_lng, radius_miles")
      .eq("user_id", context.userId)
      .maybeSingle();
    if (partner?.base_lat != null) {
      return {
        label: partner.base_postcode ?? "your area",
        lat: partner.base_lat,
        lng: partner.base_lng,
        radiusMiles: partner.radius_miles ?? FEED_RADIUS_MILES,
      };
    }
    return null;
  });

/** A partner's home base and how far they will travel. */
export const setPartnerArea = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) =>
    z
      .object({
        postcode: z.string().trim().min(3).max(10),
        radiusMiles: z.number().int().min(1).max(100),
      })
      .parse(d),
  )
  .handler(async ({ data, context }) => {
    const geo = await geocodePostcode(data.postcode);
    const { error } = await context.supabase
      .from("partner_profiles")
      .update({
        base_postcode: data.postcode.toUpperCase(),
        base_lat: geo.lat,
        base_lng: geo.lng,
        radius_miles: data.radiusMiles,
      })
      .eq("user_id", context.userId);
    if (error) return { ok: false as const, error: error.message };
    return { ok: true as const };
  });


async function listManagedHomeIds(context: any): Promise<Set<string>> {
  const [{ data: owned }, { data: shared }] = await Promise.all([
    context.supabase.from("saved_homes").select("id").eq("user_id", context.userId),
    (context.supabase as any).from("household_members").select("home_id,owner_id").eq("member_user_id", context.userId).eq("status", "active"),
  ]);
  const ids = new Set<string>((owned ?? []).map((h: any) => h.id));
  for (const membership of shared ?? []) {
    const { data: entitled } = await (context.supabase as any).rpc("has_membership_entitlement", { _user_id: membership.owner_id, _entitlement: "household_sharing" });
    if (entitled) ids.add(membership.home_id);
  }
  return ids;
}

/** Evidence-backed reminders for the signed-in customer's saved homes. */
export const listHomeServiceReminders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await (context.supabase as any)
      .from("home_service_reminders")
      .select("id, home_id, booking_id, service_id, title, due_date, notify_date, last_notified_at, source, note, status")
      .eq("user_id", context.userId)
      .eq("status", "active")
      .order("due_date");
    if (error) throw new Error(error.message);
    return data ?? [];
  });

/** Customers control reminder creation; Shields never invents a due date. */
export const createHomeServiceReminder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({
    homeId: z.string().uuid().nullable().optional(),
    bookingId: z.string().uuid().nullable().optional(),
    serviceId: z.string().uuid().nullable().optional(),
    title: z.string().trim().min(2).max(120),
    dueDate: z.string().date(),
    notifyDate: z.string().date().optional(),
    note: z.string().trim().max(500).optional(),
  }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: entitled } = await (context.supabase as any).rpc("has_membership_entitlement", {
      _user_id: context.userId,
      _entitlement: "home_reminders",
    });
    if (!entitled) return { ok: false as const, error: "Shields Home or higher is required for maintenance reminders" };
    if (data.homeId) {
      const { data: home, error: homeError } = await context.supabase
        .from("saved_homes")
        .select("id")
        .eq("id", data.homeId)
        .eq("user_id", context.userId)
        .maybeSingle();
      if (homeError) return { ok: false as const, error: homeError.message };
      if (!home) return { ok: false as const, error: "That property does not belong to your account" };
    }
    if (data.bookingId) {
      const { data: booking, error: bookingError } = await context.supabase
        .from("bookings")
        .select("id")
        .eq("id", data.bookingId)
        .eq("customer_id", context.userId)
        .maybeSingle();
      if (bookingError) return { ok: false as const, error: bookingError.message };
      if (!booking) return { ok: false as const, error: "That booking does not belong to your account" };
    }
    if (data.notifyDate && data.notifyDate > data.dueDate) {
      return { ok: false as const, error: "The reminder notification date must be on or before the due date" };
    }

    const { data: reminder, error } = await (context.supabase as any)
      .from("home_service_reminders")
      .insert({
        user_id: context.userId,
        home_id: data.homeId ?? null,
        booking_id: data.bookingId ?? null,
        service_id: data.serviceId ?? null,
        title: data.title,
        due_date: data.dueDate,
        notify_date: data.notifyDate ?? data.dueDate,
        source: "customer_set",
        note: data.note || null,
      })
      .select("id")
      .single();
    if (error || !reminder) return { ok: false as const, error: error?.message ?? "Could not create reminder" };

    await (context.supabase as any).from("home_service_reminder_events").insert({
      reminder_id: reminder.id,
      user_id: context.userId,
      event_type: "created",
      detail: { due_date: data.dueDate, notify_date: data.notifyDate ?? data.dueDate, source: "customer_set" },
    });
    return { ok: true as const, reminderId: reminder.id as string };
  });

export const dismissHomeServiceReminder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ reminderId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const now = new Date().toISOString();
    const { data: reminder, error } = await (context.supabase as any)
      .from("home_service_reminders")
      .update({ status: "dismissed", dismissed_at: now, updated_at: now })
      .eq("id", data.reminderId)
      .eq("user_id", context.userId)
      .select("id")
      .maybeSingle();
    if (error) return { ok: false as const, error: error.message };
    if (!reminder) return { ok: false as const, error: "Reminder not found" };
    await (context.supabase as any).from("home_service_reminder_events").insert({
      reminder_id: data.reminderId,
      user_id: context.userId,
      event_type: "dismissed",
      detail: {},
    });
    return { ok: true as const };
  });

export const updateHomeServiceReminder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({
    reminderId: z.string().uuid(),
    title: z.string().trim().min(2).max(120),
    dueDate: z.string().date(),
    notifyDate: z.string().date(),
    note: z.string().trim().max(500).optional(),
  }).parse(value))
  .handler(async ({ data, context }) => {
    if (data.notifyDate > data.dueDate) {
      return { ok: false as const, error: "The reminder notification date must be on or before the due date" };
    }
    const { data: reminder, error } = await (context.supabase as any)
      .from("home_service_reminders")
      .update({
        title: data.title,
        due_date: data.dueDate,
        notify_date: data.notifyDate,
        note: data.note || null,
        last_notified_at: null,
        updated_at: new Date().toISOString(),
      })
      .eq("id", data.reminderId)
      .eq("user_id", context.userId)
      .select("id")
      .maybeSingle();
    if (error) return { ok: false as const, error: error.message };
    if (!reminder) return { ok: false as const, error: "Reminder not found" };
    await (context.supabase as any).from("home_service_reminder_events").insert({
      reminder_id: data.reminderId,
      user_id: context.userId,
      event_type: "updated",
      detail: { due_date: data.dueDate, notify_date: data.notifyDate },
    });
    return { ok: true as const };
  });

export const restoreHomeServiceReminder = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ reminderId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: reminder, error } = await (context.supabase as any)
      .from("home_service_reminders")
      .update({ status: "active", dismissed_at: null, last_notified_at: null, updated_at: new Date().toISOString() })
      .eq("id", data.reminderId)
      .eq("user_id", context.userId)
      .select("id")
      .maybeSingle();
    if (error) return { ok: false as const, error: error.message };
    if (!reminder) return { ok: false as const, error: "Reminder not found" };
    await (context.supabase as any).from("home_service_reminder_events").insert({
      reminder_id: data.reminderId,
      user_id: context.userId,
      event_type: "restored",
      detail: {},
    });
    return { ok: true as const };
  });

export const listHomeServiceReminderHistory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await (context.supabase as any)
      .from("home_service_reminder_events")
      .select("id,reminder_id,event_type,detail,created_at,home_service_reminders(home_id,title,due_date,source)")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const listDismissedHomeServiceReminders = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await (context.supabase as any)
      .from("home_service_reminders")
      .select("id,home_id,booking_id,service_id,title,due_date,notify_date,source,note,status,dismissed_at")
      .eq("user_id", context.userId)
      .eq("status", "dismissed")
      .order("dismissed_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return data ?? [];
  });


export const listManagedHomes = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const [{ data: owned, error: ownedError }, { data: sharedRows, error: sharedError }] = await Promise.all([
      context.supabase
        .from("saved_homes")
        .select("id,label,address_line,town_city,postcode,is_active")
        .eq("user_id", context.userId)
        .order("is_active", { ascending: false })
        .order("created_at", { ascending: true }),
      (context.supabase as any)
        .from("household_members")
        .select("home_id,owner_id,household_role")
        .eq("member_user_id", context.userId)
        .eq("status", "active"),
    ]);
    if (ownedError || sharedError) throw ownedError ?? sharedError;

    // An invitation alone does not grant lasting access when the owner's
    // household-sharing entitlement has ended.
    const entitledShared = (await Promise.all((sharedRows ?? []).map(async (row: any) => {
      const { data: entitled, error } = await (context.supabase as any).rpc("has_membership_entitlement", {
        _user_id: row.owner_id,
        _entitlement: "household_sharing",
      });
      return !error && entitled ? row : null;
    }))).filter(Boolean);
    const sharedIds = entitledShared.map((row: any) => row.home_id);
    let sharedHomes: any[] = [];
    if (sharedIds.length) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data, error } = await supabaseAdmin
        .from("saved_homes")
        .select("id,label,address_line,town_city,postcode,is_active")
        .in("id", sharedIds);
      if (error) throw error;
      const roleByHome = new Map(entitledShared.map((row: any) => [row.home_id, row.household_role]));
      sharedHomes = (data ?? []).map((home: any) => ({
        ...home,
        isShared: true,
        householdRole: roleByHome.get(home.id) ?? "member",
      }));
    }

    return [
      ...(owned ?? []).map((home: any) => ({ ...home, isShared: false, householdRole: "owner" })),
      ...sharedHomes,
    ].map((home: any) => ({
      id: home.id,
      label: home.label,
      addressLine: home.address_line,
      townCity: home.town_city,
      postcode: home.postcode,
      isActive: home.is_active,
      isShared: home.isShared,
      householdRole: home.householdRole,
    }));
  });

export const listManagedHomeCalendar = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const [{ data: ownedHomes }, { data: sharedMemberships }] = await Promise.all([
      context.supabase
        .from("saved_homes")
        .select("id,label,user_id")
        .eq("user_id", context.userId),
      (context.supabase as any)
        .from("household_members")
        .select("home_id,owner_id,household_role")
        .eq("member_user_id", context.userId)
        .eq("status", "active"),
    ]);

    const homeMeta = new Map<string, { label: string; ownerId: string; shared: boolean }>();
    for (const home of ownedHomes ?? []) {
      homeMeta.set(home.id, { label: home.label, ownerId: context.userId, shared: false });
    }

    for (const membership of sharedMemberships ?? []) {
      const { data: sharing } = await (context.supabase as any).rpc("has_membership_entitlement", {
        _user_id: membership.owner_id,
        _entitlement: "household_sharing",
      });
      if (!sharing) continue;
      const { data: home } = await supabaseAdmin
        .from("saved_homes")
        .select("id,label")
        .eq("id", membership.home_id)
        .eq("user_id", membership.owner_id)
        .maybeSingle();
      if (home) homeMeta.set(home.id, { label: home.label, ownerId: membership.owner_id, shared: true });
    }

    const homeIds = [...homeMeta.keys()];
    if (!homeIds.length) return { bookings: [], reminders: [] };

    const [{ data: bookings }, { data: reminders }] = await Promise.all([
      supabaseAdmin
        .from("bookings")
        .select("id,home_id,customer_id,reference,status,preferred_date,scheduled_date,scheduled_window,services(name)")
        .in("home_id", homeIds)
        .not("status", "in", '("cancelled","declined","expired","closed")')
        .order("scheduled_date", { ascending: true, nullsFirst: false })
        .limit(200),
      (supabaseAdmin as any)
        .from("home_service_reminders")
        .select("id,home_id,user_id,title,due_date,source,status")
        .in("home_id", homeIds)
        .eq("status", "active")
        .order("due_date")
        .limit(200),
    ]);

    const safeBookings = (bookings ?? []).filter((booking: any) => {
      const meta = homeMeta.get(booking.home_id);
      return meta && booking.customer_id === meta.ownerId;
    }).map((booking: any) => ({
      id: booking.id,
      homeId: booking.home_id,
      homeLabel: homeMeta.get(booking.home_id)?.label ?? "Home",
      shared: homeMeta.get(booking.home_id)?.shared ?? false,
      reference: booking.reference,
      status: booking.status,
      serviceName: booking.services?.name ?? "Service",
      date: booking.scheduled_date ?? booking.preferred_date,
      window: booking.scheduled_window ?? null,
      canOpenBooking: booking.customer_id === context.userId,
    }));

    const safeReminders = (reminders ?? []).filter((reminder: any) => {
      const meta = homeMeta.get(reminder.home_id);
      return meta && reminder.user_id === meta.ownerId;
    }).map((reminder: any) => ({
      id: reminder.id,
      homeId: reminder.home_id,
      homeLabel: homeMeta.get(reminder.home_id)?.label ?? "Home",
      shared: homeMeta.get(reminder.home_id)?.shared ?? false,
      title: reminder.title,
      dueDate: reminder.due_date,
      source: reminder.source,
      canManage: reminder.user_id === context.userId,
    }));

    return { bookings: safeBookings, reminders: safeReminders };
  });

export const getManagedHomeOverview = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ homeId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: owned } = await context.supabase
      .from("saved_homes")
      .select("id,user_id,label,address_line,town_city,postcode")
      .eq("id", data.homeId)
      .eq("user_id", context.userId)
      .maybeSingle();

    let ownerId = context.userId;
    let home = owned as any;
    let access: "owner" | "member" | "manager" = "owner";

    if (!home) {
      const { data: membership } = await (context.supabase as any)
        .from("household_members")
        .select("owner_id,household_role,status")
        .eq("home_id", data.homeId)
        .eq("member_user_id", context.userId)
        .eq("status", "active")
        .maybeSingle();
      if (!membership) return { ok: false as const, error: "Property not found" };
      ownerId = membership.owner_id;
      access = membership.household_role === "manager" ? "manager" : "member";
      const { data: sharing } = await (context.supabase as any).rpc("has_membership_entitlement", {
        _user_id: ownerId,
        _entitlement: "household_sharing",
      });
      if (!sharing) return { ok: false as const, error: "Household sharing is not active for this property" };
      const { data: sharedHome } = await supabaseAdmin
        .from("saved_homes")
        .select("id,user_id,label,address_line,town_city,postcode")
        .eq("id", data.homeId)
        .eq("user_id", ownerId)
        .maybeSingle();
      if (!sharedHome) return { ok: false as const, error: "Property not found" };
      home = sharedHome;
    }

    const [{ data: bookings }, { data: spendEntitled }, { data: reminders }] = await Promise.all([
      supabaseAdmin
        .from("bookings")
        .select("id,reference,status,service_id,assigned_partner_id,agreed_price_pence,preferred_date,scheduled_date,scheduled_window,completed_at,created_at,services(name,slug)")
        .eq("home_id", data.homeId)
        .eq("customer_id", ownerId)
        .order("created_at", { ascending: false })
        .limit(200),
      (context.supabase as any).rpc("has_membership_entitlement", {
        _user_id: ownerId,
        _entitlement: "home_spend_insights",
      }),
      (supabaseAdmin as any)
        .from("home_service_reminders")
        .select("id,title,due_date,notify_date,source,status")
        .eq("home_id", data.homeId)
        .eq("user_id", ownerId)
        .eq("status", "active")
        .order("due_date")
        .limit(50),
    ]);

    const partnerIds = [...new Set((bookings ?? []).map((booking: any) => booking.assigned_partner_id).filter(Boolean))] as string[];
    const { data: partners } = partnerIds.length
      ? await supabaseAdmin.from("partner_profiles").select("user_id,trading_name,rating_avg,rating_count").in("user_id", partnerIds)
      : { data: [] as any[] };
    const partnerById = new Map((partners ?? []).map((partner: any) => [partner.user_id, partner]));

    const enriched = (bookings ?? []).map((booking: any) => ({
      ...booking,
      partner: booking.assigned_partner_id ? partnerById.get(booking.assigned_partner_id) ?? null : null,
    }));
    const completed = enriched.filter((booking: any) => ["completed","closed"].includes(booking.status));
    const upcoming = enriched.filter((booking: any) => !["completed","closed","cancelled","declined","expired"].includes(booking.status));
    const totalSpendPence = spendEntitled
      ? completed.reduce((sum: number, booking: any) => sum + Number(booking.agreed_price_pence ?? 0), 0)
      : null;

    const previousPartners = [...new Map(
      completed
        .filter((booking: any) => booking.assigned_partner_id)
        .map((booking: any) => [
          booking.assigned_partner_id,
          {
            partnerId: booking.assigned_partner_id,
            tradingName: booking.partner?.trading_name ?? "Previous Partner",
            rating: booking.partner?.rating_avg == null ? null : Number(booking.partner.rating_avg),
            ratingCount: Number(booking.partner?.rating_count ?? 0),
            lastBookingId: booking.id,
            serviceSlug: booking.services?.slug ?? null,
            serviceName: booking.services?.name ?? "Service",
          },
        ]),
    ).values()];

    return {
      ok: true as const,
      access,
      home: {
        id: home.id,
        label: home.label,
        addressLine: home.address_line,
        townCity: home.town_city,
        postcode: home.postcode,
      },
      upcoming,
      completed,
      reminders: reminders ?? [],
      previousPartners,
      spending: {
        entitled: Boolean(spendEntitled),
        totalPence: totalSpendPence,
        completedJobs: completed.length,
      },
    };
  });

export const prepareRepeatHomeBooking = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ bookingId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: booking } = await context.supabase
      .from("bookings")
      .select("id,customer_id,assigned_partner_id,status,service_id,services(slug)")
      .eq("id", data.bookingId)
      .eq("customer_id", context.userId)
      .maybeSingle();
    if (!booking || !["completed","closed"].includes(booking.status) || !booking.assigned_partner_id) {
      return { ok: false as const, error: "Only a completed job with an assigned Partner can be booked again" };
    }

    const { error } = await (context.supabase as any)
      .from("customer_partner_preferences")
      .upsert({
        customer_id: context.userId,
        partner_id: booking.assigned_partner_id,
        source_booking_id: booking.id,
        wants_again: true,
        updated_at: new Date().toISOString(),
      }, { onConflict: "customer_id,partner_id" });
    if (error) return { ok: false as const, error: error.message };

    return {
      ok: true as const,
      serviceSlug: (booking.services as { slug?: string } | null)?.slug ?? null,
      partnerId: booking.assigned_partner_id,
    };
  });

const homeDocumentCategory = z.enum([
  "certificate",
  "warranty",
  "manual",
  "invoice",
  "receipt",
  "photo",
  "other",
]);

export const getHomeVaultAccess = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ homeId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: owned, error: ownedError } = await context.supabase
      .from("saved_homes").select("id").eq("id", data.homeId).eq("user_id", context.userId).maybeSingle();
    if (ownedError) throw ownedError;
    let ownerId = context.userId;
    let access: "owner" | "shared" = "owner";
    if (!owned) {
      const { data: member, error: memberError } = await (context.supabase as any)
        .from("household_members").select("owner_id")
        .eq("home_id", data.homeId).eq("member_user_id", context.userId)
        .eq("status", "active").maybeSingle();
      if (memberError) throw memberError;
      if (!member) return { entitled: false, access: "none" as const };
      ownerId = member.owner_id;
      access = "shared";
    }
    const [{ data: vault, error: vaultError }, { data: sharing, error: sharingError }] = await Promise.all([
      (context.supabase as any).rpc("has_membership_entitlement", { _user_id: ownerId, _entitlement: "home_vault" }),
      access === "shared"
        ? (context.supabase as any).rpc("has_membership_entitlement", { _user_id: ownerId, _entitlement: "household_sharing" })
        : Promise.resolve({ data: true, error: null }),
    ]);
    if (vaultError || sharingError) throw vaultError ?? sharingError;
    if (!vault || !sharing) return { entitled: false, access: "none" as const };
    if (access === "shared") {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: home, error } = await supabaseAdmin.from("saved_homes")
        .select("id").eq("id", data.homeId).eq("user_id", ownerId).maybeSingle();
      if (error) throw error;
      if (!home) return { entitled: false, access: "none" as const };
    }
    return { entitled: true, access };
  });

export const listHomeDocuments = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const homes = await listManagedHomeIds(context);
    if (!homes.size) return [];
    const { data, error } = await (context.supabase as any)
      .from("home_documents")
      .select("id,user_id,home_id,booking_id,category,title,storage_path,original_name,mime_type,size_bytes,created_at")
      .in("home_id", [...homes])
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return (data ?? []).map((document: any) => ({
      id: document.id,
      home_id: document.home_id,
      booking_id: document.booking_id,
      category: document.category,
      title: document.title,
      storage_path: document.storage_path,
      original_name: document.original_name,
      mime_type: document.mime_type,
      size_bytes: document.size_bytes,
      created_at: document.created_at,
      ownedByMe: document.user_id === context.userId,
    }));
  });

export const recordHomeDocument = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) =>
    z.object({
      homeId: z.string().uuid(),
      bookingId: z.string().uuid().nullable().optional(),
      category: homeDocumentCategory,
      title: z.string().trim().min(1).max(160),
      storagePath: z.string().trim().min(3).max(500),
      originalName: z.string().trim().min(1).max(255),
      mimeType: z.string().trim().max(150).optional(),
      sizeBytes: z.number().int().min(1).max(20 * 1024 * 1024),
    }).parse(value),
  )
  .handler(async ({ data, context }) => {
    const { data: result, error } = await (context.supabase as any).rpc(
      "record_home_document_authoritative",
      {
        p_home_id: data.homeId,
        p_booking_id: data.bookingId ?? null,
        p_category: data.category,
        p_title: data.title,
        p_storage_path: data.storagePath,
        p_original_name: data.originalName,
        p_mime_type: data.mimeType || null,
        p_size_bytes: data.sizeBytes,
      },
    );
    if (error) return { ok: false as const, error: error.message };
    const response = result as { ok?: boolean; error?: string; used_bytes?: number; quota_bytes?: number } | null;
    if (!response?.ok) {
      return {
        ok: false as const,
        error: response?.error === "vault_quota_reached"
          ? "Your Home Vault has reached its safety storage limit. Remove files you no longer need before uploading another."
          : response?.error ?? "Could not save this document",
      };
    }
    return {
      ok: true as const,
      usedBytes: Number(response.used_bytes ?? 0),
      quotaBytes: Number(response.quota_bytes ?? 0),
    };
  });

export const getHomeDocumentUrl = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ documentId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const managedHomes = await listManagedHomeIds(context);
    const { data: document, error } = await (context.supabase as any)
      .from("home_documents")
      .select("storage_path,home_id,user_id")
      .eq("id", data.documentId)
      .maybeSingle();
    if (error || !document || !managedHomes.has(document.home_id)) return { ok: false as const, error: "Document not found" };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: signed, error: signError } = await supabaseAdmin.storage
      .from("home-documents")
      .createSignedUrl(document.storage_path, 600);
    if (signError || !signed?.signedUrl) {
      return { ok: false as const, error: "Could not open this document" };
    }
    const { data: ownedDocument } = await (supabaseAdmin as any)
      .from("home_documents")
      .select("id,user_id,home_id")
      .eq("id", data.documentId)
      .maybeSingle();
    if (ownedDocument?.user_id === context.userId) {
      await (supabaseAdmin as any).from("home_document_events").insert({
        document_id: ownedDocument.id,
        user_id: context.userId,
        home_id: ownedDocument.home_id,
        event_type: "opened",
        detail: {},
      });
    }
    return { ok: true as const, url: signed.signedUrl };
  });

export const deleteHomeDocument = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ documentId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: document, error: lookupError } = await (context.supabase as any)
      .from("home_documents")
      .select("id,user_id,home_id,storage_path,category,title,size_bytes")
      .eq("id", data.documentId)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (lookupError) return { ok: false as const, error: lookupError.message };
    if (!document) return { ok: false as const, error: "Document not found" };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { error: deleteError } = await (supabaseAdmin as any)
      .from("home_documents")
      .delete()
      .eq("id", data.documentId)
      .eq("user_id", context.userId);
    if (deleteError) return { ok: false as const, error: deleteError.message };

    await (supabaseAdmin as any).from("home_document_events").insert({
      document_id: document.id,
      user_id: context.userId,
      home_id: document.home_id,
      event_type: "deleted",
      detail: {
        category: document.category,
        title: document.title,
        size_bytes: document.size_bytes,
      },
    });

    const { error: storageError } = await supabaseAdmin.storage
      .from("home-documents")
      .remove([document.storage_path]);
    if (storageError) {
      await (supabaseAdmin as any).from("operational_alerts").insert({
        severity: "warning",
        title: "Home Vault storage cleanup required",
        detail: {
          document_id: document.id,
          home_id: document.home_id,
          storage_path: document.storage_path,
          error: storageError.message,
        },
      });
      return { ok: true as const, cleanupPending: true as const };
    }

    return { ok: true as const, cleanupPending: false as const };
  });

export const listHomeDocumentAudit = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await (context.supabase as any)
      .from("home_document_events")
      .select("id,document_id,home_id,event_type,detail,created_at")
      .eq("user_id", context.userId)
      .order("created_at", { ascending: false })
      .limit(100);
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const listHouseholdMembers = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ homeId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: rows, error } = await (context.supabase as any)
      .from("household_members")
      .select("id,home_id,email,display_name,household_role,status,invited_at,invite_expires_at,joined_at")
      .eq("home_id", data.homeId)
      .eq("owner_id", context.userId)
      .neq("status", "removed")
      .order("invited_at");
    if (error) throw new Error(error.message);
    return rows ?? [];
  });

export const inviteHouseholdMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) =>
    z.object({
      homeId: z.string().uuid(),
      email: z.string().email().max(200),
      displayName: z.string().trim().max(120).optional(),
      role: z.enum(["member", "manager"]),
      returnUrl: z.string().url().max(300),
    }).parse(value),
  )
  .handler(async ({ data, context }) => {
    if (!isAllowedShieldsReturnUrl(data.returnUrl)) {
      return { ok: false as const, error: "Invitation links must open Shields" };
    }
    const [{ data: entitled }, { data: home }] = await Promise.all([
      (context.supabase as any).rpc("has_membership_entitlement", {
        _user_id: context.userId,
        _entitlement: "household_sharing",
      }),
      context.supabase
        .from("saved_homes")
        .select("id,label")
        .eq("id", data.homeId)
        .eq("user_id", context.userId)
        .maybeSingle(),
    ]);
    if (!entitled) return { ok: false as const, error: "Shields Family or Complete is required for household sharing" };
    if (!home) return { ok: false as const, error: "Property not found" };

    const email = data.email.trim().toLowerCase();
    const { data: authData } = await context.supabase.auth.getUser();
    if (authData.user?.email?.trim().toLowerCase() === email) {
      return { ok: false as const, error: "You already own this home, so you cannot invite yourself" };
    }
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: invite, error } = await (supabaseAdmin as any)
      .from("household_members")
      .upsert({
        home_id: data.homeId,
        owner_id: context.userId,
        email,
        display_name: data.displayName || null,
        household_role: data.role,
        status: "invited",
        member_user_id: null,
        invited_at: new Date().toISOString(),
        invite_expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        joined_at: null,
        updated_at: new Date().toISOString(),
      }, { onConflict: "home_id,email" })
      .select("id")
      .single();
    if (error || !invite) return { ok: false as const, error: error?.message ?? "Could not create invitation" };

    try {
      const { sendTemplateEmail } = await import("@/lib/email-templates/send-email");
      await sendTemplateEmail("household_invitation", email, {
        idempotencyKey: `household-invite-${invite.id}-${Date.now()}`,
        templateData: {
          subject: "You have been invited to help manage a home in Shields",
          title: "Join this home in Shields",
          body: `You have been invited to help manage ${home.label || "a home"} in Shields. Sign in with this email address to review and accept the invitation.`,
          ctaText: "Open Shields",
          ctaUrl: data.returnUrl,
          audience: "customer",
        },
      });
    } catch (mailError) {
      console.error("[Home] household invitation email failed", mailError);
      return { ok: true as const, emailSent: false as const };
    }
    return { ok: true as const, emailSent: true as const };
  });

export const listMyHouseholdInvitations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: authData } = await context.supabase.auth.getUser();
    const email = authData.user?.email?.trim().toLowerCase();
    if (!email) return [];
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data, error } = await (supabaseAdmin as any)
      .from("household_members")
      .select("id,home_id,email,display_name,household_role,status,saved_homes(label,town_city)")
      .eq("email", email)
      .eq("status", "invited")
      .gt("invite_expires_at", new Date().toISOString());
    if (error) throw new Error(error.message);
    return data ?? [];
  });

export const acceptHouseholdInvitation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ inviteId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { data: result, error } = await (context.supabase as any).rpc(
      "accept_household_invitation",
      { p_invite_id: data.inviteId },
    );
    if (error) return { ok: false as const, error: error.message };
    return result as { ok: boolean; error?: string; home_id?: string };
  });

export const removeHouseholdMember = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((value: unknown) => z.object({ memberId: z.string().uuid() }).parse(value))
  .handler(async ({ data, context }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: removed, error } = await (supabaseAdmin as any)
      .from("household_members")
      .update({ status: "removed", updated_at: new Date().toISOString() })
      .eq("id", data.memberId)
      .eq("owner_id", context.userId)
      .neq("status", "removed")
      .select("id")
      .maybeSingle();
    if (error) return { ok: false as const, error: error.message };
    return removed
      ? { ok: true as const }
      : { ok: false as const, error: "Household member not found" };
  });
